import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  DelegationMessage,
  DelegationStats,
  DelegationValidationReport,
} from "@/types/delegations";

/**
 * The validator's verdict, for an operator about to change what the ministry
 * publishes about 4,390 named officials.
 *
 * The three severities are visually distinct on purpose. An ERROR blocks
 * publication; a WARNING is a real change needing a human decision — a quarter of
 * the records gone, a year removed, 52 cells Excel turned into dates — and is
 * overridable; INFO is the diff preview. Collapsing them into one list would let
 * «١٬٢٠٠ سجل أقل مما هو منشور» read like a progress note.
 */

/**
 * 🔴 WESTERN DIGITS, AND THAT IS THE MINISTRY'S OWN CONVENTION rather than a shortcut.
 * `mof-frontend/src/lib/obs/types.ts` states it — "years render in Arabic-Indic digits
 * (٢٠٢٤) and measures in Western digits" — and the OBS validation report next door
 * formats `en-US` for the same reason.
 *
 * ⚠️ THIS WAS `ar-EG` AND IT SHIPPED A VISIBLE DEFECT, reported on the public screen
 * first and here second: `ar-EG` groups with U+066C ARABIC THOUSANDS SEPARATOR, which
 * the Arabic UI face draws at CAP HEIGHT — «٤٬٣٩٠» reads as a stray apostrophe floating
 * above the digits instead of a grouping mark.
 * 🔑 The trap is the GROUPING, not the locale: it appears at 1,000 and above and never
 * below, which is why a version number and a page count never showed it.
 */
const fmt = new Intl.NumberFormat("en-US");

/**
 * 🔑 THE VALUE IS WRAPPED IN `<bdi>`, NOT GIVEN `dir="ltr"` — the lesson the OBS
 * report already records. `dir="ltr"` renders the digits correctly AND left-aligns
 * the block inside an otherwise RTL card, so every figure sits on the opposite edge
 * from its own label. `<bdi>` isolates the number so its digits resolve LTR while
 * the block stays RTL and the value lines up under its label.
 */
function StatCell({
  label,
  value,
  previous,
}: {
  label: string;
  value: string;
  previous?: string;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="break-all text-sm font-semibold">
        <bdi>{value}</bdi>
        {previous ? (
          <span className="ms-2 text-xs font-normal text-muted-foreground">
            (كان <bdi>{previous}</bdi>)
          </span>
        ) : null}
      </div>
    </div>
  );
}

function MessageList({
  items,
  tone,
}: {
  items: DelegationMessage[];
  tone: "error" | "warning" | "info";
}) {
  if (!items.length) return null;
  const Icon = tone === "error" ? XCircle : tone === "warning" ? AlertTriangle : Info;
  return (
    <ul className="space-y-2">
      {items.map((m) => (
        <li
          key={`${m.code}-${m.message}`}
          className={cn(
            "flex items-start gap-2 rounded-lg border p-3 text-sm",
            tone === "error" && "border-destructive/40 bg-destructive/10 text-destructive",
            tone === "warning" && "border-amber-500/40 bg-amber-500/10 text-amber-800",
            tone === "info" && "border-border bg-muted/30 text-muted-foreground",
          )}
        >
          <Icon className="mt-0.5 size-4 shrink-0" />
          <span>{m.message}</span>
        </li>
      ))}
    </ul>
  );
}

export function DelegationsValidationReport({
  report,
  previousStats,
}: {
  report: DelegationValidationReport;
  previousStats?: DelegationStats | null;
}) {
  const s: DelegationStats | undefined = report.stats;
  const blocked = report.errors.length > 0;

  return (
    <div className="space-y-4">
      <div
        className={cn(
          "flex items-start gap-2 rounded-lg border p-3 text-sm",
          blocked
            ? "border-destructive/40 bg-destructive/10 text-destructive"
            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-800",
        )}
      >
        {blocked ? (
          <XCircle className="mt-0.5 size-4 shrink-0" />
        ) : (
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
        )}
        <span>
          {blocked
            ? "الملف مرفوض — عالج الأخطاء أدناه قبل إعادة المحاولة."
            : report.warnings.length
              ? "الملف صالح، لكنه يغيّر بيانات منشورة — راجع التنبيهات قبل النشر."
              : "الملف صالح وجاهز للنشر."}
        </span>
      </div>

      {s ? (
        <div className="grid grid-cols-2 gap-2">
          <StatCell
            label="عدد السجلات"
            value={fmt.format(s.rowCount)}
            previous={previousStats ? fmt.format(previousStats.rowCount) : undefined}
          />
          <StatCell
            label="عدد السنوات"
            value={fmt.format(s.years.length)}
            previous={previousStats ? fmt.format(previousStats.years.length) : undefined}
          />
        </div>
      ) : null}

      <MessageList items={report.errors} tone="error" />
      <MessageList items={report.warnings} tone="warning" />
      <MessageList items={report.info} tone="info" />
    </div>
  );
}
